/**
 * Quote Request Application Hooks - Application Layer
 *
 * High-level quote request operations that combine infrastructure APIs with business logic.
 * Components should use these hooks instead of calling infrastructure APIs directly.
 */

import { useCallback } from "react";
import {
  useExternalEmbedControllerGetEmbedUrl,
  useExternalQuoteRequestsControllerFindAll,
  externalQuoteRequestsControllerFindAll,
  getExternalQuoteRequestsControllerFindAllQueryKey,
} from "@semo/api-contracts";
import type {
  ExternalQuoteRequestsControllerFindAllParams,
  QuoteRequestListItemDto,
} from "@semo/api-contracts";
import { EmbedService } from "../services/EmbedService";
import { useSemoInfiniteQuery } from "@semo/shared-utils";

/**
 * High-level hook for quote requests list operations
 *
 * @example
 * ```tsx
 * const { requests, isLoading, refetch } = useQuoteRequestsList();
 * ```
 */
export function useQuoteRequestsList(
  params?: ExternalQuoteRequestsControllerFindAllParams,
  options?: { query?: { staleTime?: number } },
) {
  const query = useExternalQuoteRequestsControllerFindAll(params, {
    query: options?.query,
  });

  // Extract items from the response structure: BaseResponse -> data -> items
  const requests = query.data?.data?.items || [];

  return {
    requests,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

/** 상태 탭 숫자 — 서버가 검색 조건 전체로 센 값(불러온 쪽 수가 아니다). */
export interface QuoteRequestStatusSummary {
  all: number;
  ongoing: number;
  closed: number;
}

/**
 * 공급사 목록 응답 meta 의 `ongoingCount`·`closedCount` 를 읽는다.
 *
 * 화면이 «지금까지 불러온 목록 길이» 로 탭 숫자를 세서, 처음엔 «전체 12» 였다가 끝까지 내리면
 * «전체 19» 로 바뀌었다(A3-10). 서버는 같은 검색 조건 전체를 센 값을 이미 내려 준다.
 */
export function readStatusSummary(
  meta: unknown,
): QuoteRequestStatusSummary | null {
  if (typeof meta !== "object" || meta === null) return null;
  if (!("ongoingCount" in meta) || !("closedCount" in meta)) return null;
  const { ongoingCount, closedCount } = meta;
  if (typeof ongoingCount !== "number" || typeof closedCount !== "number") {
    return null;
  }
  return {
    all: ongoingCount + closedCount,
    ongoing: ongoingCount,
    closed: closedCount,
  };
}

/**
 * High-level hook for quote requests infinite list operations
 */
export function useQuoteRequestsInfinite(
  params?: ExternalQuoteRequestsControllerFindAllParams,
  options?: { limit?: number },
) {
  const queryParams = { ...params, limit: options?.limit || 10 };
  const query = useSemoInfiniteQuery<
    QuoteRequestListItemDto,
    ExternalQuoteRequestsControllerFindAllParams
  >(
    getExternalQuoteRequestsControllerFindAllQueryKey(
      queryParams,
    ) as unknown as string[],
    (p, signal) => externalQuoteRequestsControllerFindAll(p, signal),
    queryParams,
  );

  const requests = query.data?.pages.flatMap((page) => page.items) || [];
  const statusSummary = readStatusSummary(query.data?.pages[0]?.meta);

  return {
    requests,
    statusSummary,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    error: query.error,
    refetch: query.refetch,
  };
}

/**
 * High-level hook for quote response operations
 *
 * Combines getting embed URL with opening popup window in a single operation.
 *
 * @example
 * ```tsx
 * const { openQuoteResponse, isLoading } = useQuoteResponseOperations();
 * await openQuoteResponse(requestId);
 * ```
 */
export function useQuoteResponseOperations() {
  const embedUrlMutation = useExternalEmbedControllerGetEmbedUrl();

  const openQuoteResponse = useCallback(
    async (requestId: string): Promise<void> => {
      try {
        const response = await embedUrlMutation.mutateAsync({
          data: {
            type: "quote-response",
            targetId: requestId,
            origin: window.location.origin,
          },
        });

        // Use EmbedService to handle response and open popup
        EmbedService.handleEmbedUrlResponse(response);
      } catch (error) {
        EmbedService.handleEmbedUrlError(error);
      }
    },
    [embedUrlMutation],
  );

  return {
    openQuoteResponse,
    isLoading: embedUrlMutation.isPending,
    error: embedUrlMutation.error,
  };
}

/**
 * High-level hook for opening chat from quote request
 *
 * Gets chat embed URL and opens in popup window.
 *
 * @example
 * ```tsx
 * const { openChatFromRequest, isLoading } = useChatFromQuoteRequest();
 * await openChatFromRequest(chatRoomId);
 * ```
 */
export function useChatFromQuoteRequest() {
  const embedUrlMutation = useExternalEmbedControllerGetEmbedUrl();

  const openChatFromRequest = useCallback(
    async (targetId: string): Promise<void> => {
      try {
        const response = await embedUrlMutation.mutateAsync({
          data: {
            type: "chat",
            targetId: targetId,
            origin: window.location.origin,
          },
        });

        // Use EmbedService to handle response and open popup
        EmbedService.handleEmbedUrlResponse(response);
      } catch (error) {
        EmbedService.handleEmbedUrlError(error);
      }
    },
    [embedUrlMutation],
  );

  return {
    openChatFromRequest,
    isLoading: embedUrlMutation.isPending,
    error: embedUrlMutation.error,
  };
}

/**
 * Combined hook for all quote request operations
 *
 * Provides a unified interface for quote request management.
 *
 * @example
 * ```tsx
 * const { requests, isLoading, openQuoteResponse, openChat } = useQuoteRequestOperations();
 * ```
 */
export function useQuoteRequestOperations(
  params?: ExternalQuoteRequestsControllerFindAllParams,
  options?: { query?: { staleTime?: number }; limit?: number },
) {
  const listQuery = useQuoteRequestsInfinite(params, {
    limit: options?.limit,
  });
  const { openQuoteResponse, isLoading: isOpeningResponse } =
    useQuoteResponseOperations();
  const { openChatFromRequest, isLoading: isOpeningChat } =
    useChatFromQuoteRequest();

  return {
    // List operations
    requests: listQuery.requests,
    statusSummary: listQuery.statusSummary,
    isLoading: listQuery.isLoading,
    isFetchingNextPage: listQuery.isFetchingNextPage,
    hasNextPage: listQuery.hasNextPage,
    fetchNextPage: listQuery.fetchNextPage,
    error: listQuery.error,
    refetch: listQuery.refetch,

    // Action operations
    openQuoteResponse,
    openChatFromRequest,
    isOpeningResponse,
    isOpeningChat,
  };
}
